import React from 'react';
import useUser from '../../../hooks/useUser';
import {
  Table,
  usePagination,
  Box,
  Shelf,
  TextLink,
  Stack,
  Pagination,
  Text,
  Button,
  Input,
} from 'chaser-components';
import matchSorter from 'match-sorter';
import useTableState, {
  SET_PAGE,
  Order,
  TOGGLE_ORDERING,
} from '../../../hooks/useTableState';
import searchIcon from '../../../assets/search.svg';
import TableSort from '../../../components/TableUtils/TableSort/index';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { SET_BUNDLE } from '../../login/duck/types';

function sort(data, orderedColumn, ordering) {
  if (ordering === Order.NONE) return data;

  function compare(o1, o2) {
    const o1Name = o1 || '';
    const o2Name = o2 || '';
    if (typeof o1Name === 'string' && typeof o2Name === 'string')
      return o1Name.localeCompare(o2Name);
    return o1 - o2;
  }

  return data.slice().sort((o1, o2) => {
    let comparison = compare(o1[orderedColumn], o2[orderedColumn]);
    const i = ordering === Order.ASC ? 1 : -1;
    if (comparison > 0) {
      return i;
    }
    if (comparison < 0) {
      return -i;
    }
    return 0;
  });
}

const Admin = () => {
  const user = useUser();
  const bundles = user.bundles;
  const setBundle = useDispatch();

  const [{ search, sortProp, ordering, page }, dispatch] = useTableState({
    initialOrdering: Order.ASC,
    searchProp: 1,
  });

  function onSort(prop) {
    dispatch({ type: TOGGLE_ORDERING, payload: prop });
  }

  const filtered = matchSorter(bundles, search, {
    keys: ['name', 'ShortCode'],
  });

  const sortedBundles = sort(filtered, sortProp, ordering);

  const currentSort =
    ordering !== Order.NONE
      ? `${ordering === Order.DESC ? '-' : ''}${sortProp}`
      : '';

  const columns = [
    {
      accessor: 'name',
      header: () => (
        <TableSort
          currentSort={currentSort}
          sortProp="name"
          sortFunc={onSort}
          headerTitle="Organisation name"
        />
      ),
      cell: name => <TextLink as={Link}>{name}</TextLink>,
    },
    {
      accessor: 'currency',
      header: () => (
        <TableSort
          currentSort={currentSort}
          sortProp="currency"
          sortFunc={onSort}
          headerTitle="Currency"
        />
      ),
      cell: currency => <Text>{currency}</Text>,
    },
    {
      accessor: 'currentLicenses',
      header: () => (
        <TableSort
          currentSort={currentSort}
          sortProp="currentLicenses"
          sortFunc={onSort}
          headerTitle="Current Licenses"
        />
      ),
      cell: currentLicenses => <Text>{currentLicenses}</Text>,
    },
    {
      accessor: 'totalLicenses',
      header: () => (
        <TableSort
          currentSort={currentSort}
          sortProp="totalLicenses"
          sortFunc={onSort}
          headerTitle="Total Licenses"
        />
      ),
      cell: totalLicenses => <Text>{totalLicenses}</Text>,
    },
    {
      accessor: 'cancelled',
      header: () => (
        <TableSort
          currentSort={currentSort}
          sortProp="cancelled"
          sortFunc={onSort}
          headerTitle="Cancelled"
        />
      ),
      cell: cancelled => (
        <Text>
          {cancelled
            ? new Date(cancelled).toLocaleString('en-gb', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })
            : '-'}
        </Text>
      ),
    },
    {
      accessor: '_id',
      cell: (_id, { name }) => (
        <Button
          as={Link}
          to="/clients"
          onClick={() => {
            setBundle({
              type: SET_BUNDLE,
              data: {
                bundle: _id,
                bundleName: name,
              },
            });
          }}
          size="narrow">
          Join
        </Button>
      ),
    },
  ];

  const pagination = usePagination({
    manual: false,
    data: sortedBundles,
    totalSize: sortedBundles.length,
    page,
    pageSize: 10,
    onPageChange: p => dispatch({ type: SET_PAGE, payload: p }),
  });

  return (
    <Box py="medium">
      <Stack>
        <Shelf gap="xsmall">
          <Input
            icon={searchIcon}
            value={search}
            onChange={e =>
              dispatch({ type: 'SET_SEARCH', payload: e.target.value })
            }
          />
          <Text color="Mid Neutral 1">
            Showing {pagination.itemCountOnPage} of {sortedBundles.length || 0}
          </Text>
        </Shelf>
        <Stack gap="large">
          <Table
            keyField="_id"
            data={sortedBundles}
            columns={columns}
            pagination={pagination}
            noDataIndication="No customers"
            loading={!sortedBundles?.length}
          />
          <Pagination pagination={pagination} />
        </Stack>
      </Stack>
    </Box>
  );
};

export default Admin;
